/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module components/ProposalRejectionView/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../../utils/logger.js";
import React, { useState, useRef, useEffect } from "react";
import styles from "./index.module.css";
import { useNavigate } from "react-router";
import { handleRejectProposal } from "../../logic/proposal/index.js";
import { useTranslation } from "react-i18next";
import { IconButton } from "@mui/material";
import { ReactComponent as ChevronLeftIcon } from "../../assets/icons/UserViews/ChevronLeftIcon.svg";

const RejectProposalView = ({ onBack, proposal, onSend, isStarred }) => {
  const [rejectionReason, setRejectionReason] = useState("");
  const dialogRef = useRef(null);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleSend = async () => {
    try {
      await handleRejectProposal(
        proposal._id,
        rejectionReason,
        isStarred,
        navigate,
      );
      onSend(proposal._id, rejectionReason);
      onBack();
    } catch (error) {
      logger.error("Error rejecting proposal:", { err: sanitizeError(error) });
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.backNavigation}>
        <IconButton onClick={onBack}>
          <ChevronLeftIcon styles={{}} />
        </IconButton>
        <h2 className={styles.mainTitle}>
          {t("components.rejectProposalModal.title")}
        </h2>
      </div>

      <div className={styles.content}>
        <p className={styles.instructionText}>
          {t("components.rejectProposalModal.instructionText1")}:
        </p>
        <p>
          {t("components.rejectProposalModal.description")}:{" "}
          {proposal.descripcion}
        </p>
        <p>
          {t("components.rejectProposalModal.deliveryTime")}:{" "}
          {proposal.tiempoDeEntrega}
        </p>
        <p>
          {t("components.rejectProposalModal.cost")}: {proposal.costo}
        </p>

        <p className={styles.instructionText}>
          {t("components.rejectProposalModal.instructionText2")}
        </p>

        <div className={styles.rejectionReasonContainer}>
          <p className={styles.instructionText}>
            {t("components.rejectProposalModal.rejectReason")}
          </p>
          <textarea
            className={styles.rejectionReason}
            placeholder={t(
              "components.rejectProposalModal.rejectReasonPlaceholder",
            )}
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
          />
        </div>

        <div className={styles.modalActions}>
          <button className={styles.cancelButton} onClick={onBack}>
            {t("components.rejectProposalModal.cancelButton")}
          </button>
          <button
            className={styles.sendButton}
            onClick={handleSend}
            disabled={!rejectionReason.trim()}
          >
            {t("components.rejectProposalModal.sendButton")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RejectProposalView;
